"use client";

import { buildItem, trackSearch, trackViewItemList, useListImpression } from "@v2/lib/analytics";
import { useCallback } from "react";
import { PLPSiteProduct } from "../types";

/**
 * Reports the first page of a listing: which products were put in front of the
 * shopper, in which order, and under what heading.
 *
 * It waits until the list is actually on screen, so a page nobody scrolled
 * through does not count as an impression and drag the click rate down.
 *
 * Later pages are not its business — PlpLoadMore holds them in its own state
 * and reports them itself.
 *
 * Each site product is represented by its first variant, which is the one the
 * card shows and prices — the same one select_item reports if the card is
 * clicked.
 */
export default function PlpListAnalytics({ siteProducts, listId, listName, searchTerm }: {
    siteProducts: PLPSiteProduct[];
    listId: string;
    listName: string;
    searchTerm?: string;
}) {
    const report = useCallback(() => {
        if (searchTerm) trackSearch(searchTerm);

        trackViewItemList(
            siteProducts.map((siteProduct, index) => {
                const variant = siteProduct.details?.[0];

                return buildItem({
                    variantId: variant?.product_id ?? siteProduct.id,
                    name: variant?.name ?? siteProduct.title,
                    brand: siteProduct.brand?.en_name,
                    tomanPrice: variant?.price,
                    tomanDiscount: variant?.discount,
                    siteProductId: siteProduct.id,
                    index,
                    listId,
                    listName,
                });
            }),
            listId,
            listName,
        );
    }, [siteProducts, searchTerm, listId, listName]);

    const sentinelRef = useListImpression(report, siteProducts.length > 0);

    return <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />;
}
